/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getCluTestModelService } from 'src/clu/core/services/test/cluTestModelService';
import { CluTestConversationModelSwagger, CluTestOrchestrationModelSwagger } from 'src/clu/core/services/test/cluTestModelSwagger';
import { CluTestModelResults } from 'src/clu/core/services/test/types';
import {
    cluConversationTestModelToTestJobInfo,
    cluOrchestrationTestModelToTestJobInfo
} from 'src/clu/modules/handlers/testModel/cluTestModelMappers';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';

export type CluTestModelHandlerGroup = ReturnType<typeof createCluTestModelHandlerGroup>;

/**
 * Represents a handler group for clu model testing using runtime.
 *
 * @param dependencies The handler group dependencies.
 */
export const createCluTestModelHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const testCluModel = async ({
        id,
        context,
        culture,
        testQuery,
        deploymentName,
        setTestResults
    }: HandlerParams<{
        id: string;
        testQuery: string;
        culture: string;
        deploymentName: string;
        setTestResults: (testReults: CluTestModelResults) => void;
    }>) => {
        const { name: projectName, projectType, deploymentsStoreHouse: deployments } = rootStore.currentCluProjectStore;
        const deployment = deployments.findById(deploymentName);

        if (!deployment) {
            return;
        }

        const testSwagger = await getCluTestModelService({ context }).test({ id, testQuery, culture, projectName, deploymentName });

        if (projectType === 'Conversation') {
            setTestResults({
                originalJsonResult: testSwagger,
                ...cluConversationTestModelToTestJobInfo(<CluTestConversationModelSwagger>testSwagger)
            });
        } else {
            setTestResults({
                originalJsonResult: testSwagger,
                ...cluOrchestrationTestModelToTestJobInfo(<CluTestOrchestrationModelSwagger>testSwagger)
            });
        }
    };

    return { testCluModel };
};
